import React, { useEffect, useState, useCallback } from "react";
import { Check, Calculator, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, Tooltip, CartesianGrid, ComposedChart, Area, Line, XAxis, YAxis, Legend,
} from "recharts";
import { api, formatApiError, API } from "../../lib/api";
import { GBP, GBP2 } from "../../lib/format";
import { Field } from "./utils";

const fmtPct = (v) => (typeof v === "number" ? `${v.toFixed(2)}%` : "—");
const fmtX = (v) => (typeof v === "number" ? `${v.toFixed(2)}×` : "—");

function Section({ label, title, children, action }) {
  return (
    <section className="swiss-card-flat p-6">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label">{label}</div>
          {title && <div className="font-display text-lg font-semibold mt-1">{title}</div>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, sub, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-[var(--surface)] border-[var(--line)]",
    ink: "bg-[var(--ink)] text-white",
    accent: "bg-[var(--accent)] text-white",
    warm: "bg-[var(--warm-soft)] border-[var(--warm)]",
  };
  return (
    <div className={`p-4 rounded-sm border ${tones[tone]}`} data-testid={testid}>
      <div className="label" style={tone === "neutral" || tone === "warm" ? {} : { color: "rgba(255,255,255,0.78)" }}>{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs muted mt-1" style={tone === "neutral" || tone === "warm" ? {} : { color: "rgba(255,255,255,0.85)" }}>{sub}</div>}
    </div>
  );
}

export default function InvestorModel({ project }) {
  const [assumptions, setAssumptions] = useState(null);
  const [result, setResult] = useState(null);
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [computing, setComputing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/indurent-model`);
      setAssumptions(data.assumptions);
      setSeeded(data.seeded);
      const r = await api.post(`/projects/${project.id}/indurent-model/compute`, data.assumptions);
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setComputing(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/indurent-model/compute`, assumptions);
      setResult(data);
      toast.success("Model recomputed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setComputing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/indurent-model`, assumptions);
      setSeeded(false);
      toast.success("Model saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    if (seeded) { toast.error("Save the model first"); return; }
    window.open(`${API}/projects/${project.id}/indurent-model/pdf`, "_blank");
  };

  const update = (path, val) => {
    setAssumptions(a => {
      const next = { ...a };
      if (path.startsWith("opex.")) {
        next.opex = { ...a.opex, [path.slice(5)]: Number(val) };
      } else {
        next[path] = path === "useful_life_years" || path === "inverter_replacement_year"
          ? Number.parseInt(val || 0, 10)
          : Number(val);
      }
      return next;
    });
  };

  if (!assumptions) {
    return <div className="muted font-mono text-xs uppercase tracking-widest p-6">Loading investor model…</div>;
  }

  const out = result?.outputs;
  const totals = result?.totals;
  const cf = result?.cashflow || [];

  return (
    <div className="space-y-6" data-testid="investor-model-tab">
      {/* ===== Toolbar ===== */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label">Indurent investor model</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-1">
            Full {assumptions.useful_life_years}-year financial breakdown
          </div>
          {seeded && (
            <div className="text-xs mt-2 px-2 py-1 inline-block rounded-sm" style={{ background: "var(--warm-soft)", color: "var(--warm-hover)" }}>
              Seeded from project data — save to persist customisations.
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={compute} disabled={computing} className="btn-ghost" data-testid="im-compute-btn">
            <RefreshCw size={13} className={computing ? "animate-spin" : ""} /> Recompute
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="im-save-btn">
            <Check size={14} /> {saving ? "Saving…" : "Save model"}
          </button>
          <button onClick={downloadPdf} className="btn-accent" data-testid="im-pdf-btn">
            <FileDown size={14} /> Investor PDF
          </button>
        </div>
      </div>

      {/* ===== Headline KPIs ===== */}
      {out && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="im-headline-kpis">
          <Kpi label="CapEx" tone="ink" testid="im-kpi-capex"
               value={GBP.format(assumptions.initial_outlay_gbp || 0)} />
          <Kpi label={`NPV @ ${assumptions.discount_rate_pct}%`} tone="accent" testid="im-kpi-npv"
               value={GBP.format(out.npv_gbp || 0)} />
          <Kpi label="Payback" testid="im-kpi-payback"
               value={out.payback_years != null ? `${out.payback_years.toFixed(2)} yr` : "—"} />
          <Kpi label="Yield on cost" tone="warm" testid="im-kpi-yield"
               value={fmtPct(out.yield_on_cost_pct)} sub="Year 1 net / CapEx" />
        </div>
      )}

      {/* ===== Exit IRRs ===== */}
      {out && (
        <Section label="Exit scenarios" title="IRR & equity multiple at each hold period">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="im-exit-irrs">
            {["3", "5", "10", "25"].map((ey) => (
              <Kpi key={ey} label={`${ey}-year exit`}
                   testid={`im-exit-${ey}`}
                   value={fmtPct(out.exit_irrs_pct?.[ey])}
                   sub={`Multiple ${fmtX(out.exit_equity_multiples?.[ey])}`} />
            ))}
          </div>
          <div className="text-[11px] muted mt-3">
            Exit IRR assumes a terminal value of {assumptions.exit_multiple_x_revenue}× that year&apos;s gross revenue, added to net cashflow in the exit year.
          </div>
        </Section>
      )}

      {/* ===== Assumptions ===== */}
      <Section label="Assumptions" title="Plant & generation">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Field label="Nameplate (kWp)" testid="im-f-kwp">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.nameplate_kwp}
                   onChange={(e) => update("nameplate_kwp", e.target.value)} data-testid="im-nameplate-input" />
          </Field>
          <Field label="Year-1 generation (kWh)" testid="im-f-gen">
            <input className="input-swiss" type="number" value={assumptions.annual_generation_kwh}
                   onChange={(e) => update("annual_generation_kwh", e.target.value)} data-testid="im-annual-gen-input" />
          </Field>
          <Field label="Degradation (%/yr)" testid="im-f-deg">
            <input className="input-swiss" type="number" step="0.05" value={assumptions.annual_degradation_pct}
                   onChange={(e) => update("annual_degradation_pct", e.target.value)} data-testid="im-deg-input" />
          </Field>
          <Field label="Useful life (yr)" testid="im-f-life">
            <input className="input-swiss" type="number" value={assumptions.useful_life_years}
                   onChange={(e) => update("useful_life_years", e.target.value)} data-testid="im-life-input" />
          </Field>
        </div>
      </Section>

      <Section label="Assumptions" title="Pricing & revenue">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Field label="BTM consumption (%)" testid="im-f-btm">
            <input className="input-swiss" type="number" step="0.5" value={assumptions.consumed_on_site_pct}
                   onChange={(e) => update("consumed_on_site_pct", e.target.value)} data-testid="im-btm-input" />
          </Field>
          <Field label="Customer price (p/kWh)" testid="im-f-cust">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.customer_price_p_per_kwh}
                   onChange={(e) => update("customer_price_p_per_kwh", e.target.value)} data-testid="im-cust-price-input" />
          </Field>
          <Field label="Export price (p/kWh)" testid="im-f-exp">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.export_price_p_per_kwh}
                   onChange={(e) => update("export_price_p_per_kwh", e.target.value)} data-testid="im-export-input" />
          </Field>
          <Field label="Admin fee (p/kWh)" testid="im-f-adm">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.admin_fee_p_per_kwh}
                   onChange={(e) => update("admin_fee_p_per_kwh", e.target.value)} data-testid="im-admin-input" />
          </Field>
          <Field label="Subsidy (£/MWh)" testid="im-f-sub">
            <input className="input-swiss" type="number" step="0.5" value={assumptions.subsidy_gbp_per_mwh}
                   onChange={(e) => update("subsidy_gbp_per_mwh", e.target.value)} data-testid="im-subsidy-input" />
          </Field>
          <Field label="Energy inflation (%)" testid="im-f-einf">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.energy_inflation_pct}
                   onChange={(e) => update("energy_inflation_pct", e.target.value)} data-testid="im-energy-inf-input" />
          </Field>
          <Field label="Vacancy (%)" testid="im-f-vac">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.vacancy_pct}
                   onChange={(e) => update("vacancy_pct", e.target.value)} data-testid="im-vacancy-input" />
          </Field>
          <Field label="Bad debt (%)" testid="im-f-bd">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.bad_debt_pct}
                   onChange={(e) => update("bad_debt_pct", e.target.value)} data-testid="im-bad-debt-input" />
          </Field>
        </div>
      </Section>

      <Section label="Assumptions" title="Opex breakdown (Year 1, £)">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {["insurance", "maintenance", "monitoring", "management", "metering", "cleaning"].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)} testid={`im-f-opex-${k}`}>
              <input className="input-swiss" type="number" step="100"
                     value={assumptions.opex?.[`${k}_gbp`] ?? 0}
                     onChange={(e) => update(`opex.${k}_gbp`, e.target.value)}
                     data-testid={`im-opex-${k}-input`} />
            </Field>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-5">
          <Field label="Opex inflation (%/yr)" testid="im-f-oinf">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.opex_inflation_pct}
                   onChange={(e) => update("opex_inflation_pct", e.target.value)} data-testid="im-opex-inf-input" />
          </Field>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Total Year-1 opex</div>
            <div className="font-mono mt-1" data-testid="im-opex-total">
              {GBP.format(
                ["insurance","maintenance","monitoring","management","metering","cleaning"]
                  .reduce((s,k) => s + Number(assumptions.opex?.[`${k}_gbp`] || 0), 0)
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section label="Assumptions" title="Tax & CapEx">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Field label="Tax (%)" testid="im-f-tax">
            <input className="input-swiss" type="number" step="0.5" value={assumptions.tax_pct}
                   onChange={(e) => update("tax_pct", e.target.value)} data-testid="im-tax-input" />
          </Field>
          <Field label="Export tax (%)" testid="im-f-etax">
            <input className="input-swiss" type="number" step="0.5" value={assumptions.export_tax_pct}
                   onChange={(e) => update("export_tax_pct", e.target.value)} data-testid="im-export-tax-input" />
          </Field>
          <Field label="Discount rate (%)" testid="im-f-disc">
            <input className="input-swiss" type="number" step="0.1" value={assumptions.discount_rate_pct}
                   onChange={(e) => update("discount_rate_pct", e.target.value)} data-testid="im-discount-input" />
          </Field>
          <Field label="Exit multiple (× revenue)" testid="im-f-em">
            <input className="input-swiss" type="number" step="0.5" value={assumptions.exit_multiple_x_revenue}
                   onChange={(e) => update("exit_multiple_x_revenue", e.target.value)} data-testid="im-exit-mult-input" />
          </Field>
          <Field label="Initial CapEx (£)" testid="im-f-cap">
            <input className="input-swiss" type="number" value={assumptions.initial_outlay_gbp}
                   onChange={(e) => update("initial_outlay_gbp", e.target.value)} data-testid="im-capex-input" />
          </Field>
          <Field label="Inverter replacement (£)" testid="im-f-inv">
            <input className="input-swiss" type="number" value={assumptions.inverter_replacement_gbp}
                   onChange={(e) => update("inverter_replacement_gbp", e.target.value)} data-testid="im-inv-cost-input" />
          </Field>
          <Field label="Inverter replacement year" testid="im-f-invy">
            <input className="input-swiss" type="number" value={assumptions.inverter_replacement_year}
                   onChange={(e) => update("inverter_replacement_year", e.target.value)} data-testid="im-inv-year-input" />
          </Field>
        </div>
      </Section>

      {/* ===== Revenue / Opex / Cumulative chart ===== */}
      {cf.length > 0 && (
        <Section label="Visual" title="Revenue mix & cumulative cashflow">
          <div style={{ width: "100%", minWidth: 0, height: 360 }}>
            <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1}>
              <ComposedChart data={cf} margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
                <defs>
                  <linearGradient id="gradCust" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00D6DF" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#00D6DF" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="gradExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#314041" stopOpacity={0.85} />
                    <stop offset="100%" stopColor="#314041" stopOpacity={0.45} />
                  </linearGradient>
                  <linearGradient id="gradAdm" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B33C86" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#B33C86" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="gradSub" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A78BFA" stopOpacity={0.85} />
                    <stop offset="100%" stopColor="#A78BFA" stopOpacity={0.45} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => v >= 1000 ? `£${(v/1000).toFixed(0)}k` : `£${v}`}
                       label={{ value: "Annual revenue", angle: -90, position: "insideLeft", offset: 8, style: { fontSize: 10, fill: "#44595A" } }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => v >= 1000 || v <= -1000 ? `£${(v/1000).toFixed(0)}k` : `£${v}`}
                       label={{ value: "Cumulative CF", angle: 90, position: "insideRight", offset: 8, style: { fontSize: 10, fill: "#44595A" } }} />
                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 11 }}
                         formatter={(v, n) => [GBP2.format(v), n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area yAxisId="left" type="monotone" dataKey="customer_revenue_gbp" name="Customer (BTM)"
                      stackId="rev" stroke="#00D6DF" fill="url(#gradCust)" strokeWidth={1} />
                <Area yAxisId="left" type="monotone" dataKey="export_revenue_gbp" name="Grid export"
                      stackId="rev" stroke="#314041" fill="url(#gradExp)" strokeWidth={1} />
                <Area yAxisId="left" type="monotone" dataKey="admin_fee_gbp" name="Admin fee"
                      stackId="rev" stroke="#B33C86" fill="url(#gradAdm)" strokeWidth={1} />
                <Area yAxisId="left" type="monotone" dataKey="subsidy_revenue_gbp" name="Subsidy"
                      stackId="rev" stroke="#A78BFA" fill="url(#gradSub)" strokeWidth={1} />
                <Line yAxisId="right" type="monotone" dataKey="cumulative_cashflow_gbp" name="Cumulative CF"
                      stroke="#0A0A0A" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] muted mt-2">
            Stacked revenue mix per year (BTM + export + admin fee + subsidy) overlaid with the black cumulative-cashflow line crossing zero at payback ({out?.payback_years != null ? `Yr ${out.payback_years.toFixed(2)}` : "—"}).
          </div>
        </Section>
      )}

      {/* ===== Year-by-year table ===== */}
      {cf.length > 0 && (
        <Section label={`${assumptions.useful_life_years}-year operating cashflow`} title="Detailed projections">
          <div className="overflow-x-auto">
            <table className="data-table text-xs" data-testid="im-cashflow-table">
              <thead>
                <tr>
                  <th className="num">Yr</th>
                  <th className="num">Gen (kWh)</th>
                  <th className="num">Cust £</th>
                  <th className="num">Export £</th>
                  <th className="num">Admin £</th>
                  <th className="num">Subsidy £</th>
                  <th className="num">Total rev</th>
                  <th className="num">Opex</th>
                  <th className="num">Bad debt</th>
                  <th className="num">Tax</th>
                  <th className="num">Net CF</th>
                  <th className="num">Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {cf.map((r) => (
                  <tr key={r.year} data-testid={`im-cf-row-${r.year}`}>
                    <td className="num font-mono">{r.year}</td>
                    <td className="num font-mono">{Math.round(r.post_deg_kwh).toLocaleString()}</td>
                    <td className="num font-mono">{GBP.format(r.customer_revenue_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.export_revenue_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.admin_fee_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.subsidy_revenue_gbp)}</td>
                    <td className="num font-mono font-semibold">{GBP.format(r.total_revenue_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.opex_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.bad_debt_gbp)}</td>
                    <td className="num font-mono">{GBP.format(r.tax_gbp)}</td>
                    <td className={`num font-mono ${r.net_cashflow_gbp < 0 ? "text-red-600" : ""}`}>
                      {GBP.format(r.net_cashflow_gbp)}
                    </td>
                    <td className={`num font-mono font-semibold ${r.cumulative_cashflow_gbp < 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {GBP.format(r.cumulative_cashflow_gbp)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr className="bg-zinc-50 font-semibold">
                    <td colSpan={6} className="num">Total</td>
                    <td className="num font-mono">{GBP.format(totals.total_revenue_gbp)}</td>
                    <td className="num font-mono">{GBP.format(totals.total_opex_gbp)}</td>
                    <td className="num font-mono"></td>
                    <td className="num font-mono">{GBP.format(totals.total_tax_gbp)}</td>
                    <td className="num font-mono">{GBP.format(totals.total_net_cashflow_gbp)}</td>
                    <td className="num font-mono"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Section>
      )}

      <div className="text-[11px] muted text-center py-2 flex items-center justify-center gap-1">
        <Calculator size={11} /> All figures pre-leverage. Forecasts depend on irradiance, customer load & tariff conditions.
      </div>
    </div>
  );
}
